import { TriangleAlert } from "lucide-react";
import type { WriteRejection } from "@estelle/domain";

import { Button } from "@/components/ui/button";
import { describeRejection } from "@/modules/warehouses/writeRejection";

/**
 * Every `WriteRejection` kind rendered as what it actually is, shared by
 * create, dispatch, receive and cancel below.
 *
 * `conflict` and `invalid` get bespoke treatment because they carry
 * structured data a flattened string would throw away — a reload
 * affordance, a per-field message. A transfer is ERP-owned rather than
 * mirrored (`Transfer` carries no `channelUpdatedAt` the way `Warehouse`
 * does), so `conflict` here reads as "someone else changed this transfer",
 * not "changed in Shopify" — the wording `catalog/screens/HsnEditor.tsx`
 * uses for a mirrored field would be a false claim about where the change
 * happened. `forbidden` cannot occur from these actions today, but the
 * union still has no `default`: a seventh kind fails this file at compile
 * time instead of silently rendering nothing.
 */
export function RejectionNotice({
  rejection,
  onReload,
}: {
  rejection: WriteRejection;
  onReload: () => void;
}) {
  if (rejection.kind === "conflict") {
    return (
      <div className="text-destructive flex items-start gap-1.5 text-xs">
        <TriangleAlert className="mt-0.5 size-3.5 shrink-0" aria-hidden />
        <div className="space-y-1">
          <p>
            Someone else changed this transfer at {rejection.currentUpdatedAt} —
            after this screen loaded. Reload to see its current state before
            trying again.
          </p>
          <Button type="button" size="xs" variant="outline" onClick={onReload}>
            Reload
          </Button>
        </div>
      </div>
    );
  }

  if (rejection.kind === "invalid") {
    return (
      <p className="text-destructive text-xs">
        {rejection.message}
        {rejection.fields
          ? " " +
            Object.entries(rejection.fields)
              .map(([field, problem]) => `${field}: ${problem}`)
              .join("; ")
          : null}
      </p>
    );
  }

  return (
    <p className="text-destructive text-xs">{describeRejection(rejection)}</p>
  );
}
